import React, { useState, useEffect } from 'react';
import { whatsappAPI } from '../../services/api';
import { toast } from 'react-toastify';

const WhatsAppAnalytics = () => {
  const [analytics, setAnalytics] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [timeRange, setTimeRange] = useState('7d');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = async () => {
    setIsLoading(true);
    try {
      const [
        otpStats,
        voiceStats,
        paymentStats,
        aiStats,
        ticketStats,
        groupStats
      ] = await Promise.all([
        whatsappAPI.getOtpAnalytics(timeRange),
        whatsappAPI.getVoiceAnalytics(timeRange),
        whatsappAPI.getPaymentAnalytics(timeRange),
        whatsappAPI.getAiAnalytics(timeRange),
        whatsappAPI.getTicketAnalytics(timeRange),
        whatsappAPI.getGroupAnalytics(1, timeRange)
      ]);

      // Calculate general stats from individual analytics
      const generalStats = {
        total_messages: 0,
        message_growth: 0,
        total_users: 0,
        user_growth: 0,
        total_revenue: 0,
        revenue_growth: 0,
        avg_response_time: 0,
        response_time_growth: 0
      };

      // Normalize OTP analytics
      let otpSummary = {};
      if (otpStats.data?.data && Array.isArray(otpStats.data.data)) {
        const total_sent = otpStats.data.data.reduce((sum, item) => sum + (item.total_sent || 0), 0);
        const verified = otpStats.data.data.reduce((sum, item) => sum + (item.verified || 0), 0);
        const expired = otpStats.data.data.reduce((sum, item) => sum + (item.expired || 0), 0);
        otpSummary = {
          total_sent,
          verified,
          expired,
          success_rate: total_sent ? verified / total_sent : 0
        };
        generalStats.total_messages += total_sent;
      }

      // Normalize Voice analytics (object form)
      let voiceSummary = {};
      if (voiceStats.data?.data) {
        if (Array.isArray(voiceStats.data.data)) {
          const total_processed = voiceStats.data.data.reduce((sum, item) => sum + (item.total_processed || 0), 0);
          voiceSummary = { total_processed, accuracy_rate: 0, avg_processing_time: 0, failed: 0 };
          generalStats.total_messages += total_processed;
        } else {
          // Backend returns object format: {total_processed, accuracy_rate, avg_processing_time, failed}
          const data = voiceStats.data.data;
          voiceSummary = {
            total_processed: data.total_processed || 0,
            accuracy_rate: data.accuracy_rate || 0,
            avg_processing_time: data.avg_processing_time || 0,
            failed: data.failed || 0
          };
          generalStats.total_messages += data.total_processed || 0;
        }
      }

      // Normalize Payment analytics (array per gateway)
      let paymentSummary = {};
      if (paymentStats.data?.data && Array.isArray(paymentStats.data.data)) {
        const total_links = paymentStats.data.data.reduce((sum, item) => sum + (item.total_links || 0), 0);
        const paid = paymentStats.data.data.reduce((sum, item) => sum + (item.successful_payments || 0), 0);
        const total_amount = paymentStats.data.data.reduce((sum, item) => sum + (Number(item.total_amount) || 0), 0);
        const conversion_rate = total_links ? paid / total_links : 0;
        paymentSummary = { total_links, paid, total_amount, conversion_rate };
        generalStats.total_revenue += total_amount;
      }

      // Normalize AI stats (sentiment + translation counts)
      let aiSummary = {};
      if (aiStats.data?.data) {
        const sentiment = Array.isArray(aiStats.data.data.sentiment) ? aiStats.data.data.sentiment : [];
        const translation = Array.isArray(aiStats.data.data.translation) ? aiStats.data.data.translation : [];
        const sentiment_analysis = sentiment.reduce((sum, item) => sum + (item.count || 0), 0);
        const translations = translation.reduce((sum, item) => sum + (item.count || 0), 0);
        const total_processed = sentiment_analysis + translations;
        const accuracy_rate = (() => {
          const scores = [
            ...sentiment.map(s => Number(s.avg_score) || 0),
            ...translation.map(t => Number(t.avg_confidence) || 0)
          ].filter(n => n > 0);
          if (!scores.length) return 0;
          return scores.reduce((a, b) => a + b, 0) / scores.length;
        })();
        aiSummary = { total_processed, accuracy_rate, sentiment_analysis, translations };
      }

      // Normalize Group analytics (array per day)
      let groupSummary = {};
      if (groupStats.data?.data && Array.isArray(groupStats.data.data)) {
        const total_messages = groupStats.data.data.reduce((sum, item) => sum + (item.total_messages || 0), 0);
        const active_groups = groupStats.data.data.reduce((sum, item) => sum + (item.active_groups || 0), 0);
        groupSummary = { total_messages, total_groups: active_groups };
        generalStats.total_messages += total_messages;
      }

      setAnalytics({
        otp: otpSummary,
        voice: voiceSummary,
        payment: paymentSummary,
        ai: aiSummary,
        tickets: ticketStats.data?.data || {},
        groups: groupSummary,
        general: generalStats
      });
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setIsLoading(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num || 0);
  };

  const formatPercentage = (num) => {
    return `${((num || 0) * 100).toFixed(1)}%`;
  };

  const getGrowthColor = (growth) => {
    if (growth > 0) return 'text-green-600 dark:text-green-400';
    if (growth < 0) return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  const getGrowthIcon = (growth) => {
    if (growth > 0) return '↗️';
    if (growth < 0) return '↘️';
    return '→';
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'messages', label: 'Messages', icon: '💬' },
    { id: 'features', label: 'Features', icon: '⚡' },
    { id: 'performance', label: 'Performance', icon: '📈' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:p-6">
              <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between">
        <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Messages</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                      {formatNumber(analytics.general?.total_messages || 0)}
                    </p>
        </div>
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                    <span className="text-xl sm:text-2xl">💬</span>
        </div>
      </div>
                <div className="mt-4 flex items-center text-sm">
                  <span className={getGrowthColor(analytics.general?.message_growth)}>
                    {getGrowthIcon(analytics.general?.message_growth)}
                  </span>
                  <span className={`ml-1 ${getGrowthColor(analytics.general?.message_growth)}`}>
                    {formatPercentage(Math.abs(analytics.general?.message_growth || 0))}
          </span>
                  <span className="ml-2 text-gray-500 dark:text-gray-400">vs last period</span>
        </div>
    </div>

              <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Users</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                      {formatNumber(analytics.general?.active_users || 0)}
                    </p>
              </div>
                  <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
                    <span className="text-xl sm:text-2xl">👥</span>
          </div>
        </div>
                <div className="mt-4 flex items-center text-sm">
                  <span className={getGrowthColor(analytics.general?.user_growth)}>
                    {getGrowthIcon(analytics.general?.user_growth)}
                  </span>
                  <span className={`ml-1 ${getGrowthColor(analytics.general?.user_growth)}`}>
                    {formatPercentage(Math.abs(analytics.general?.user_growth || 0))}
                  </span>
                  <span className="ml-2 text-gray-500 dark:text-gray-400">vs last period</span>
        </div>
      </div>

              <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between">
        <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Delivery Rate</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                      {formatPercentage(analytics.general?.delivery_rate || 0)}
          </p>
        </div>
                  <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-full">
                    <span className="text-xl sm:text-2xl">📨</span>
                  </div>
                </div>
                <div className="mt-4 flex items-center text-sm">
                  <span className={getGrowthColor(analytics.general?.delivery_growth)}>
                    {getGrowthIcon(analytics.general?.delivery_growth)}
                  </span>
                  <span className={`ml-1 ${getGrowthColor(analytics.general?.delivery_growth)}`}>
                    {formatPercentage(Math.abs(analytics.general?.delivery_growth || 0))}
                  </span>
                  <span className="ml-2 text-gray-500 dark:text-gray-400">vs last period</span>
        </div>
      </div>

              <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Response Rate</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                      {formatPercentage(analytics.general?.response_rate || 0)}
                    </p>
                  </div>
                  <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-full">
                    <span className="text-xl sm:text-2xl">💬</span>
                  </div>
                </div>
                <div className="mt-4 flex items-center text-sm">
                  <span className={getGrowthColor(analytics.general?.response_growth)}>
                    {getGrowthIcon(analytics.general?.response_growth)}
                  </span>
                  <span className={`ml-1 ${getGrowthColor(analytics.general?.response_growth)}`}>
                    {formatPercentage(Math.abs(analytics.general?.response_growth || 0))}
                  </span>
                  <span className="ml-2 text-gray-500 dark:text-gray-400">vs last period</span>
                </div>
              </div>
      </div>

            {/* Feature Usage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:p-6">
              <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Feature Usage</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-lg mr-3">🔐</span>
                      <span className="font-medium text-gray-900 dark:text-white">OTP Verification</span>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900 dark:text-white">{formatNumber(analytics.otp?.total_sent || 0)}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{formatPercentage(analytics.otp?.success_rate || 0)} success</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-lg mr-3">🎤</span>
                      <span className="font-medium text-gray-900 dark:text-white">Voice Processing</span>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900 dark:text-white">{formatNumber(analytics.voice?.total_processed || 0)}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{formatPercentage(analytics.voice?.accuracy_rate || 0)} accuracy</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-lg mr-3">💳</span>
                      <span className="font-medium text-gray-900 dark:text-white">Payment Links</span>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900 dark:text-white">{formatNumber(analytics.payment?.total_links || 0)}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{formatPercentage(analytics.payment?.conversion_rate || 0)} conversion</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-lg mr-3">🤖</span>
                      <span className="font-medium text-gray-900 dark:text-white">AI Features</span>
              </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900 dark:text-white">{formatNumber(analytics.ai?.total_processed || 0)}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{formatPercentage(analytics.ai?.accuracy_rate || 0)} accuracy</p>
              </div>
            </div>
          </div>
        </div>

              <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Support Metrics</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-lg mr-3">🎫</span>
                      <span className="font-medium text-gray-900 dark:text-white">Support Tickets</span>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900 dark:text-white">{formatNumber(analytics.tickets?.total_tickets || 0)}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{formatPercentage(analytics.tickets?.resolution_rate || 0)} resolved</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-lg mr-3">👥</span>
                      <span className="font-medium text-gray-900 dark:text-white">Groups</span>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900 dark:text-white">{formatNumber(analytics.groups?.total_groups || 0)}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{formatNumber(analytics.groups?.total_members || 0)} members</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-lg mr-3">⏱️</span>
                      <span className="font-medium text-gray-900 dark:text-white">Avg Response Time</span>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900 dark:text-white">{analytics.general?.avg_response_time || '0'} min</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">24/7 support</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-lg mr-3">⭐</span>
                      <span className="font-medium text-gray-900 dark:text-white">Customer Satisfaction</span>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900 dark:text-white">{analytics.general?.satisfaction_score || '0'}/5</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Based on ratings</p>
                    </div>
              </div>
            </div>
          </div>
        </div>
      </div>
        );

      case 'messages':
        return (
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Message Analytics</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:p-6 mb-6">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">{formatNumber(analytics.general?.messages_sent || 0)}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Messages Sent</p>
                </div>
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">{formatNumber(analytics.general?.messages_delivered || 0)}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Messages Delivered</p>
                </div>
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-purple-600 dark:text-purple-400">{formatNumber(analytics.general?.messages_read || 0)}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Messages Read</p>
        </div>
      </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3">Message Types</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Text Messages</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatNumber(analytics.general?.text_messages || 0)}</span>
                    </div>
            <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Media Messages</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatNumber(analytics.general?.media_messages || 0)}</span>
              </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Template Messages</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatNumber(analytics.general?.template_messages || 0)}</span>
            </div>
            <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Interactive Messages</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatNumber(analytics.general?.interactive_messages || 0)}</span>
                    </div>
                  </div>
              </div>

                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3">Delivery Status</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Delivered</span>
                      <span className="font-medium text-green-600 dark:text-green-400">{formatPercentage(analytics.general?.delivery_rate || 0)}</span>
            </div>
            <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Failed</span>
                      <span className="font-medium text-red-600 dark:text-red-400">{formatPercentage(analytics.general?.failure_rate || 0)}</span>
              </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Pending</span>
                      <span className="font-medium text-yellow-600 dark:text-yellow-400">{formatPercentage(analytics.general?.pending_rate || 0)}</span>
            </div>
            <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Read</span>
                      <span className="font-medium text-blue-600 dark:text-blue-400">{formatPercentage(analytics.general?.read_rate || 0)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'features':
        return (
          <div className="space-y-6">
            {/* OTP Analytics */}
            <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <span className="mr-2">🔐</span> OTP Verification Analytics
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">{formatNumber(analytics.otp?.total_sent || 0)}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Sent</p>
                </div>
                <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <p className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">{formatNumber(analytics.otp?.verified || 0)}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Verified</p>
                </div>
                <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <p className="text-xl sm:text-2xl font-bold text-yellow-600 dark:text-yellow-400">{formatPercentage(analytics.otp?.success_rate || 0)}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Success Rate</p>
                </div>
                <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <p className="text-xl sm:text-2xl font-bold text-red-600 dark:text-red-400">{formatNumber(analytics.otp?.expired || 0)}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Expired</p>
                </div>
              </div>
            </div>

            {/* Voice Processing Analytics */}
            <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <span className="mr-2">🎤</span> Voice Processing Analytics
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <p className="text-xl sm:text-2xl font-bold text-purple-600 dark:text-purple-400">{formatNumber(analytics.voice?.total_processed || 0)}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Processed</p>
                </div>
                <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <p className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">{formatPercentage(analytics.voice?.accuracy_rate || 0)}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Accuracy</p>
                </div>
                <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">{analytics.voice?.avg_processing_time || '0'}s</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Avg Processing Time</p>
                </div>
                <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <p className="text-xl sm:text-2xl font-bold text-yellow-600 dark:text-yellow-400">{formatNumber(analytics.voice?.failed || 0)}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Failed</p>
                </div>
              </div>
            </div>

            {/* Payment Analytics */}
            <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <span className="mr-2">💳</span> Payment Links Analytics
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <p className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">{formatNumber(analytics.payment?.total_links || 0)}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Links Created</p>
                </div>
                <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">{formatNumber(analytics.payment?.paid || 0)}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Paid</p>
                </div>
                <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <p className="text-xl sm:text-2xl font-bold text-yellow-600 dark:text-yellow-400">{formatPercentage(analytics.payment?.conversion_rate || 0)}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Conversion Rate</p>
              </div>
                <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <p className="text-xl sm:text-2xl font-bold text-purple-600 dark:text-purple-400">₹{formatNumber(analytics.payment?.total_amount || 0)}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Amount</p>
            </div>
          </div>
        </div>

            {/* AI Features Analytics */}
            <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <span className="mr-2">🤖</span> AI Features Analytics
          </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                  <p className="text-xl sm:text-2xl font-bold text-indigo-600 dark:text-indigo-400">{formatNumber(analytics.ai?.total_processed || 0)}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Processed</p>
                </div>
                <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <p className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">{formatPercentage(analytics.ai?.accuracy_rate || 0)}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Accuracy</p>
                </div>
                <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">{formatNumber(analytics.ai?.sentiment_analysis || 0)}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Sentiment Analysis</p>
                </div>
                <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <p className="text-xl sm:text-2xl font-bold text-yellow-600 dark:text-yellow-400">{formatNumber(analytics.ai?.translations || 0)}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Translations</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'performance':
        return (
          <div className="space-y-6">
            {/* Performance Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:p-6">
              <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg Response Time</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                      {analytics.general?.avg_response_time || '0'} min
                    </p>
                  </div>
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                    <span className="text-xl sm:text-2xl">⏱️</span>
                  </div>
                </div>
            </div>

              <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Uptime</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                      {formatPercentage(analytics.general?.uptime || 0)}
                    </p>
                  </div>
                  <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
                    <span className="text-xl sm:text-2xl">🟢</span>
                  </div>
                </div>
            </div>

              <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Error Rate</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                      {formatPercentage(analytics.general?.error_rate || 0)}
                    </p>
                  </div>
                  <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
                    <span className="text-xl sm:text-2xl">⚠️</span>
                  </div>
                </div>
            </div>

              <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">API Calls</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                      {formatNumber(analytics.general?.api_calls || 0)}
                    </p>
                  </div>
                  <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-full">
                    <span className="text-xl sm:text-2xl">🔌</span>
            </div>
          </div>
        </div>
      </div>

            {/* Support Performance */}
            <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Support Performance</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3">Ticket Metrics</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Total Tickets</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatNumber(analytics.tickets?.total_tickets || 0)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Resolved</span>
                      <span className="font-medium text-green-600 dark:text-green-400">{formatNumber(analytics.tickets?.resolved || 0)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Pending</span>
                      <span className="font-medium text-yellow-600 dark:text-yellow-400">{formatNumber(analytics.tickets?.pending || 0)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Resolution Rate</span>
                      <span className="font-medium text-blue-600 dark:text-blue-400">{formatPercentage(analytics.tickets?.resolution_rate || 0)}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3">Response Metrics</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Avg Response Time</span>
                      <span className="font-medium text-gray-900 dark:text-white">{analytics.general?.avg_response_time || '0'} min</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">First Response</span>
                      <span className="font-medium text-gray-900 dark:text-white">{analytics.general?.first_response_time || '0'} min</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Resolution Time</span>
                      <span className="font-medium text-gray-900 dark:text-white">{analytics.general?.resolution_time || '0'} hours</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Customer Satisfaction</span>
                      <span className="font-medium text-gray-900 dark:text-white">{analytics.general?.satisfaction_score || '0'}/5</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return <div>Select a tab to view content</div>;
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">WhatsApp Analytics</h1>
            <p className="text-gray-600 dark:text-gray-400">Comprehensive analytics and insights for WhatsApp features</p>
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="1d">Last 24 hours</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>
            <button
              onClick={loadAnalytics}
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-4 sm:p-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppAnalytics;
